import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { AccountFeatureAPI } from 'api/account-feature';
import { toastService } from 'components/molecules/toast';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { TranslationKey, useTranslate } from 'hooks/use-translation';
import {
  CREDIT_NOTE_SETTINGS_QUERY_KEY,
  useCreditNoteEssentialSettingsQuery,
  useUpdateCreditNoteEssentialSettingMutation,
} from 'queries/credit-note-settings';
import { CreditNoteConfiguration } from './types';

interface UpdatePayload {
  payload: Partial<CreditNoteConfiguration>;
  onError?: () => void;
}

export const useFeatureSettings = (): [boolean, (state: boolean) => void] => {
  const translate = useTranslate();

  const [featureEnabled, setFeatureEnabled] = useAccountFeature(
    AccountFeature.CreditNotes,
  );

  const onFeatureStateChange = useCallback(
    (newState: boolean) => {
      setFeatureEnabled(newState);
      if (newState) {
        AccountFeatureAPI.enableFeature(AccountFeature.CreditNotes)
          .then(() => {
            toastService({
              title: translate('success' as TranslationKey),
              message: translate(
                'settings.credit_notes.enable.success' as TranslationKey,
              ),
            }).success();
          })
          .catch(() => {
            setFeatureEnabled(!newState);
            toastService({
              title: translate('failure' as TranslationKey),
              message: translate(
                'settings.credit_notes.enable.failure' as TranslationKey,
              ),
            }).error();
          });
      } else {
        AccountFeatureAPI.disableFeature(AccountFeature.CreditNotes)
          .then(() => {
            toastService({
              title: translate('success' as TranslationKey),
              message: translate(
                'settings.credit_notes.disable.success' as TranslationKey,
              ),
            }).success();
          })
          .catch(() => {
            setFeatureEnabled(!newState);
            toastService({
              title: translate('failure' as TranslationKey),
              message: translate(
                'settings.credit_notes.disable.failure' as TranslationKey,
              ),
            }).error();
          });
      }
    },
    [setFeatureEnabled, translate],
  );

  return [featureEnabled, onFeatureStateChange];
};

export const useUpdateSettings = (): [
  (state: UpdatePayload) => void,
  boolean,
] => {
  const { data: config } = useCreditNoteEssentialSettingsQuery();
  const { mutateAsync, isLoading } =
    useUpdateCreditNoteEssentialSettingMutation();
  const translate = useTranslate();
  const queryClient = useQueryClient();

  const update = ({ payload, onError }: UpdatePayload) => {
    mutateAsync({
      payload: {
        ...config,
        ...payload,
      },
    })
      .then((data) => {
        queryClient.setQueryData<CreditNoteConfiguration>(
          CREDIT_NOTE_SETTINGS_QUERY_KEY,
          data,
        );
      })
      .catch((error) => {
        toastService({
          title: translate('settings.credit_notes.updation.failure.title'),
          message:
            error.response?.data?.errors?.[0]?.reason ??
            translate('settings.credit_notes.updation.failure.message'),
        }).error();
        onError?.();
      });
  };

  return [update, isLoading];
};
